import { errorTypes } from 'commons/error'
import { useDispatch } from 'react-redux'
import effects from 'app/effects/effects'
import MessageAlert from 'app/components/MessageAlert'
import React, { useState, useRef, useCallback, Fragment } from 'react'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import { validateEmtpyValueFormsy } from 'commons/validateEmtpyValueFormsy'
import { useDefaultLanguage } from 'commons/useDefaultLanguage'
import { humanizedTimeIndicator } from 'libs/humanizedTimeIndicator/humanizedTimeIndicator'

import {
  Button,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  TextField,
  Tooltip,
} from '@material-ui/core'
import Formsy from 'formsy-react'
import { TextFieldFormsy } from '@fuse'
import { useIntl, FormattedMessage } from 'react-intl'
import themesConfig from 'app/fuse-configs/themesConfig'
import ReplyCard from './ReplyCard'
import { ButtonWithLoading } from 'app/components/FormsyHelperComponent'
import ReplyIcon from '@material-ui/icons/Reply'
import CommentReplyMenu from 'app/main/messages/notificationTimeline/commentReplay/CommentReplyMenu'
import { useIsPaidPlan } from 'app/auth/paidPlanAccess'
import PersonAvatar from './PersonAvatar'

const useStyles = makeStyles((theme) => ({
  editingField: {
    '& .MuiInputBase-input.MuiInput-input.MuiInputBase-inputMultiline.MuiInput-inputMultiline':
      {
        fontSize: '14px',
      },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      backgroundColor: '#fff',
    },
  },
  labelOverlay: {
    '& .MuiFormLabel-root': {
      backgroundColor: '#fff',
    },
  },
  submitEdit: {
    fontSize: '1rem',
  },
  avatarIcon: {
    width: '32px',
    height: '32px',
    fontSize: '14px',
  },
  editIcon: {
    color: '#a0a0a0',
    display: 'flex',
    opacity: '0',
    visibility: 'hidden',
    alignSelf: 'center',
    position: 'relative',
    cursor: 'pointer',
    marginLeft: '8px',
  },
  commentBoxcontainer: {
    display: 'flex',
    '&:hover :nth-child(4)': {
      display: 'flex',
      opacity: '1',
      visibility: 'visible',
    },
  },
  commentBoxHide: {
    '&:hover :nth-child(4)': {
      display: 'flex',
      opacity: '0',
      visibility: 'hidden',
      marginLeft: '10px',
    },
  },
  commentTextfield: {
    width: 'auto',
    color: '#000000a8',
    fontSize: '9px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  defaultComment: {
    fontSize: '14px',
    color: theme.palette.darkGrey.main,
    margin: '5px 0',
  },
  hideEdit: {
    display: 'none',
  },
  showEdit: {
    marginLeft: '5rem',
    display: 'flex',
  },
}))

//entityId = R.prop('id', notification)
const CommentCard = ({
  item,
  entityId,
  userData,
  handleCommentCountFromReply,
  onUpdateCommentSuccess,
  onDeleteComment,
  onReplySuccess,
  onReplyUpdateSuccess,
  deleteComment,
}) => {
  const defaultLanguage = useDefaultLanguage()
  //MARK:- Display reply textfield
  const [replying, setReplying] = useState(false)
  //MARK:- Validation reply text
  const [isReplyValid, setIsReplyValid] = useState(false)
  //MARK:- Submitting reply
  const [isSubmittingReply, setIsSubmittingReply] = useState(false)
  //MARK:- Open delete dialog
  const [isOpenDeletingDialog, setIsOpenDeletingDialog] = useState(false)
  //MARK:- Set editing
  const [isEditing, setIsEditing] = useState(false)
  const [isSubmitEditing, setIsSubmitEditing] = useState(false)
  const [updatedText, setUpdatedText] = useState('')

  const [isEditBoxOpen, setIsEditBoxOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  const isPaidPlan = useIsPaidPlan()

  const classes = useStyles()
  const replyRef = useRef()
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const creatorName = R.path(['creator', 'name'], item)
  const creatorId = R.path(['creator', 'id'], item)
  const creatorType = R.path(['creator', 'type'], item)
  const userId = R.prop('id', userData)
  const replyCount = R.length(item.replies)
  const isCreator = creatorId === userId

  const createAt = humanizedTimeIndicator({
    timestamp: R.prop('createdAt', item),
    languageCode: R.prop('code', defaultLanguage),
    formatMessage: formatMessage,
  })

  //MARK:- Close delete dialog
  const handleClose = () => {
    setIsOpenDeletingDialog(false)
  }

  const editCommentBoxClose = () => {
    setIsEditBoxOpen(null)
  }

  const activeEditing = () => {
    setIsEditing(true)
    setIsEditBoxOpen(null)
  }

  const activeDeleting = () => {
    setIsEditBoxOpen(null)
    setIsOpenDeletingDialog(true)
  }

  const handleEditPopover = (e) => {
    setAnchorEl(e.currentTarget)
    setIsEditBoxOpen(true)
  }

  const handleDeleteComment = () => {
    deleteComment(item.id)
    setIsOpenDeletingDialog(false)
  }

  const handleDeleteReply = (replyId) => {
    deleteComment(replyId)
    setIsOpenDeletingDialog(false)
  }

  const onReply = useCallback(() => {
    handleCommentCountFromReply()
  }, [handleCommentCountFromReply])

  const checkCommentEntityType = useCallback(() => {
    const NOTIFICATION_COMMENT = 2
    if (entityId) {
      return NOTIFICATION_COMMENT
    }
  }, [entityId])

  const submitEditing = () => {
    setIsEditing(false)
    setIsSubmitEditing(true)
    effects.apis
      .updateComment({
        id: item.id,
        text: updatedText,
      })
      .then(() => {
        onUpdateCommentSuccess()
      })
      .catch((error) => {
        return dispatch(
          MessageAlert.showNetworkError(formatMessage)(
            error,
            errorTypes.default,
          ),
        )
      })
      .finally(() => {
        setIsSubmitEditing(false)
      })
  }

  function handleEnterToSubmit(e) {
    const ENTER_KEY = 'Enter'
    if (e.key === ENTER_KEY && e.shiftKey) {
      return
    } else if (e.key === ENTER_KEY) {
      e.preventDefault()
      validateEmptyReplyComment()
    }
  }

  function handleEnterToEdit(e) {
    const ENTER_KEY = 'Enter'
    if (e.key === ENTER_KEY && e.shiftKey) {
      return
    } else if (e.key === ENTER_KEY) {
      e.preventDefault()
      validateEmptyEditing()
    }
  }

  function validateEmptyEditing() {
    if (R.compose(R.not, R.anyPass([R.isNil, R.isEmpty]))(updatedText)) {
      submitEditing()
    } else {
      cancelEditing()
    }
  }

  function validateEmptyReplyComment() {
    if (
      R.compose(
        R.not,
        R.anyPass([R.isNil, R.isEmpty]),
      )(replyRef.current.getModel().comment)
    ) {
      createReply()
    } else {
      return
    }
  }

  const parentId = R.prop('id', item)

  const createReply = useCallback(() => {
    setIsSubmittingReply(true)

    effects.apis
      .createComment({
        creatorId: userId,
        creatorType,
        entityId,
        entityType: checkCommentEntityType(),
        text: replyRef.current.getModel().comment,
        parentId,
      })
      .then(() => {
        setIsSubmittingReply(false)
        setIsReplyValid(false)
        replyRef.current.reset()
        onReply()
        onReplySuccess()
        setReplying(false)
      })
      .catch((error) => {
        setIsSubmittingReply(false)
        return dispatch(
          MessageAlert.showNetworkError(formatMessage)(
            error,
            errorTypes.default,
          ),
        )
      })
  }, [
    userId,
    creatorType,
    entityId,
    checkCommentEntityType,
    formatMessage,
    parentId,
    onReply,
    onReplySuccess,
    dispatch,
  ])

  function cancelEditing() {
    setIsEditing(false)
    setUpdatedText(item.text)
  }

  function disableButton() {
    setIsReplyValid(false)
  }

  function enableButton() {
    setIsReplyValid(true)
  }

  function renderReplyList() {
    return item.replies.map((reply) => {
      return (
        <ReplyCard
          reply={reply}
          key={reply.id}
          userId={userId}
          handleDeleteReply={handleDeleteReply}
          onReplyUpdateSuccess={onReplyUpdateSuccess}
          notificationId={entityId}
        />
      )
    })
  }

  //MARK:- Return Statement
  return (
    <Fragment>
      <div className="flex flex-col">
        <div
          className={
            isEditing
              ? clsx(classes.commentBoxHide)
              : clsx(classes.commentBoxcontainer)
          }
        >
          <PersonAvatar
            placeHolder={R.path(['creator', 'avatarCharacters'], item)}
            subId={item.creator.id}
            alt={item.creator.name}
            src={item.creator.avatar}
            className={clsx(classes.avatarIcon, 'ltr:mr-5 rtl:ml-5')}
          />
          <div
            className={isEditing ? 'hidden' : clsx(classes.commentTextfield)}
          >
            <span>
              {formatMessage(
                { id: 'notificationTimeline.card.comment.creator.name' },
                { creatorName, createAt },
              )}
            </span>
            <p className={clsx(classes.defaultComment)}>
              {updatedText || item.text}
            </p>
          </div>
          <Formsy
            className={isEditing ? 'w-full' : 'hidden'}
            onKeyPress={handleEnterToEdit}
          >
            <TextField
              label={formatMessage(
                { id: 'notificationTimeline.card.comment.creator.name' },
                { creatorName, createAt },
              )}
              defaultValue={item.text}
              value={R.isEmpty(updatedText) ? item.text : updatedText}
              className={clsx(classes.editingField, 'w-full')}
              margin="normal"
              multiline
              variant="outlined"
              onChange={(e) => setUpdatedText(e.target.value)}
              id="editing-reply-input"
            />
          </Formsy>
          <Tooltip title="Edit or delete this" arrow="true">
            <Icon
              onClick={(e) => (!isPaidPlan ? null : handleEditPopover(e))}
              className={
                isCreator
                  ? clsx(
                      classes.editIcon,
                      !isPaidPlan ? 'cursor-default' : null,
                    )
                  : 'invisible'
              }
            >
              more_horiz
            </Icon>
          </Tooltip>
          <CommentReplyMenu
            isEditBoxOpen={isEditBoxOpen}
            anchorEl={anchorEl}
            onClose={editCommentBoxClose}
            onClickEdit={activeEditing}
            onClickDelete={activeDeleting}
          />
        </div>
        <div
          className={isEditing ? 'flex justify-end' : 'flex justify-between'}
        >
          {/*Reply Button */}
          <div
            className={clsx(
              (isEditing ? 'hidden' : 'flex', isPaidPlan || 'cursor-pointer'),
              'flex',
            )}
          >
            <ReplyIcon
              color="secondary"
              style={{ transform: 'rotate(180deg)' }}
            />
            <Button
              size="small"
              disabled={!isPaidPlan}
              style={{
                color: themesConfig.default.palette.primary.main,
                fontSize: '1rem',
              }}
              onClick={() => (!isPaidPlan ? null : setReplying(true))}
            >
              <FormattedMessage
                id="notificationTimeline.card.comment.reply.button"
                values={{ replyCount }}
              />
            </Button>
          </div>

          <div className={isEditing ? 'cursor-pointer' : 'hidden'}>
            <Button
              size="small"
              color="secondary"
              disabled={isSubmitEditing || R.isEmpty(updatedText)}
              onClick={submitEditing}
              className={clsx(classes.submitEdit, 'md:hidden')}
            >
              <FormattedMessage id="notificationTimeline.card.comment.editSubmit.button" />
            </Button>

            <Button
              size="small"
              style={{
                fontSize: '1rem',
              }}
              onClick={cancelEditing}
            >
              <FormattedMessage id="action.cancel" />
            </Button>
          </div>
        </div>

        <div style={{ marginLeft: '5rem' }}>{renderReplyList()}</div>
        {/*Reply Section */}
        <div style={{ marginLeft: '3.5rem' }}>
          <CardContent className={replying ? '' : 'hidden'}>
            <div className="flex">
              <PersonAvatar
                placeHolder={R.prop('avatarCharacters', userData)}
                subId={userData.id}
                alt={userData.firstName}
                src={userData.avatar}
                className={clsx(classes.avatarIcon, 'ltr:mr-5 rtl:ml-5')}
              />
              <Formsy
                ref={replyRef}
                className="w-full"
                onValid={enableButton}
                onInvalid={disableButton}
                onSubmit={createReply}
                onKeyPress={handleEnterToSubmit}
              >
                <TextFieldFormsy
                  name="comment"
                  id="outlined-textarea"
                  label={formatMessage({
                    id: 'notificationTimeline.card.comment.reply.label',
                  })}
                  multiline
                  className={clsx(classes.labelOverlay, 'w-full')}
                  margin="normal"
                  variant="outlined"
                  validations={{
                    isNotEmpty: validateEmtpyValueFormsy,
                  }}
                  disabled={isSubmittingReply}
                />
                <div className="flex justify-end md:hidden">
                  <ButtonWithLoading
                    type="submit"
                    color="secondary"
                    variant="contained"
                    className="my-5"
                    isLoading={isSubmittingReply}
                    disabled={!isReplyValid || isSubmittingReply}
                    labelId="notificationTimeline.card.comment.post.reply.button"
                  />
                </div>
              </Formsy>
            </div>
          </CardContent>
        </div>
      </div>
      {/* This is Dialog */}
      <Dialog
        open={isOpenDeletingDialog}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth={true}
      >
        <DialogTitle color="secondary">
          <FormattedMessage id="notificationTimeline.card.comment.delete.dialog.title" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage id="notificationTimeline.card.comment.delete.dialog.description" />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            <FormattedMessage id="action.cancel" />
          </Button>
          <Button
            style={{ color: themesConfig.default.palette.textDanger.main }}
            onClick={handleDeleteComment}
            autoFocus
          >
            <FormattedMessage id="action.delete" />
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  )
}

CommentCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number.isRequired,
    text: PropTypes.string.isRequired,
    avatar: PropTypes.object,
    replies: PropTypes.array,
    creator: PropTypes.shape({
      name: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      type: PropTypes.number.isRequired,
    }),
  }),
  onUpdateCommentSuccess: PropTypes.func.isRequired,
  onDeleteComment: PropTypes.func.isRequired,
  onReplySuccess: PropTypes.func.isRequired,
  onReplyUpdateSuccess: PropTypes.func.isRequired,
  userData: PropTypes.object.isRequired,
  handleCommentCountFromReply: PropTypes.func.isRequired,
}

export default CommentCard
